import React, { useEffect } from 'react';
import SEO from './SEO';
import { trackEvent } from './analytics';

/**
 * Thank You / Confirmation Page
 * 
 * This page is displayed after a successful contact form submission.
 * Use this URL for Google Ads conversion tracking: /thank-you
 * 
 * Google Ads Setup:
 * 1. Create a conversion action in Google Ads
 * 2. Set the conversion to track page loads
 * 3. Use URL: https://freedomcg.net/thank-you
 * 4. Or add the Google Ads conversion snippet to this page
 */

function ThankYou() {

  useEffect(() => {
    // Track the conversion event
    trackEvent('conversion', {
      event_category: 'Form',
      event_label: 'contact_form_success',
      conversion_type: 'lead'
    });

    // Also push to dataLayer for GTM/Google Ads
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'form_submission_complete',
        conversion_type: 'contact_form',
        page_path: '/thank-you'
      });
    }

    // Google Ads conversion tracking snippet placeholder
    // Uncomment and add your conversion ID and label from Google Ads
    /*
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-XXXXXXXXXX/XXXXXXXXXXXXXXXX',
        'value': 1.0,
        'currency': 'USD'
      });
    }
    */
  }, []);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* SEO - noindex to prevent this page from appearing in search results */}
      <SEO 
        title="Thank You"
        description="Thank you for contacting Freedom Contracting Group. We'll be in touch soon."
        path="/thank-you"
        noindex={true}
      />

      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <a href="/" className="flex items-center space-x-3">
              <img 
                src="/logo.webp" 
                alt="Freedom Contracting Group Logo" 
                className="h-16 sm:h-20 w-auto object-contain" 
              />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">
                  <span className="text-red-600">Freedom</span>
                  <span className="text-blue-900"> Contracting Group</span>
                </h1>
                <p className="text-xs text-gray-600 font-medium">Built on American Values</p>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
          
          {/* Success Icon */}
          <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-8">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>

          {/* Thank You Message */}
          <h1 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            Thank You for Contacting Us!
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            We've received your message and appreciate you reaching out. A member of our team will 
            get back to you within <strong>1 business day</strong>.
          </p>

          {/* What to Expect */}
          <div className="bg-slate-50 rounded-xl p-6 md:p-8 mb-8 text-left">
            <h2 className="text-xl font-bold text-blue-900 mb-4 text-center">What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">We Review Your Inquiry</h3>
                  <p className="text-gray-600">Our team will carefully review your message and project requirements.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Personal Follow-Up</h3>
                  <p className="text-gray-600">A project specialist will reach out to discuss your needs in detail.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-bold text-sm">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Free Consultation</h3>
                  <p className="text-gray-600">We'll schedule a consultation to understand your vision and provide a quote.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mb-8">
            <p className="text-gray-600 mb-4">Need to reach us sooner?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+13126756000"
                className="inline-flex items-center justify-center gap-2 text-blue-900 hover:text-red-600 font-semibold transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                (312) 675-6000
              </a>
              <span className="hidden sm:inline text-gray-400">|</span>
              <a 
                href="mailto:hello@freedomcg.net"
                className="inline-flex items-center justify-center gap-2 text-blue-900 hover:text-red-600 font-semibold transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                hello@freedomcg.net
              </a>
            </div>
          </div>

          {/* Return Home Button */}
          <a 
            href="/"
            className="inline-flex items-center justify-center gap-2 bg-blue-900 hover:bg-red-600 text-white font-semibold py-4 px-8 rounded-xl transition-colors shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
            </svg>
            Return to Homepage
          </a>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-red-600 mb-2">20+</div>
            <div className="text-gray-600">Years Experience</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-red-600 mb-2">1000+</div>
            <div className="text-gray-600">Projects Completed</div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
            <div className="text-gray-600">Client Satisfaction</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-950 via-gray-900 to-red-950 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2026 Freedom Contracting Group. Proudly building America's future, one project at a time.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ThankYou;
